import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import {
  getAllEnquiries, deleteEnquiry,
  getPendingTestimonies, approveTestimony, deleteTestimony,
  getAllToppers, deleteTopper, addTopper,
  getAllFaculty, deleteFaculty, addFaculty,
  adminLogout
} from '../firebase/firestoreService';

/* ── Reusable input style ── */
const inp = {
  width: '100%', padding: '9px 12px', borderRadius: '4px',
  border: '1.5px solid var(--ngt-border)', fontFamily: 'inherit',
  fontSize: '0.9rem', marginBottom: '0.8rem', display: 'block',
};

/* ── Add Topper Form ─────────────────────────────────────────── */
const AddTopperForm = ({ onAdded }) => {
  const empty = { name: '', year: '', percentage: '', course: '', school: '' };
  const [form, setForm] = useState(empty);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.percentage || !form.course) {
      setMsg('error:Name, percentage and course are required.');
      return;
    }
    setSaving(true);
    setMsg('');
    try {
      const doc = await addTopper({ ...form, imageFile });
      setMsg('success:Topper added successfully!');
      setForm(empty);
      setImageFile(null);
      onAdded({ id: doc.id, ...form, imageURL: '' });
    } catch (err) {
      setMsg('error:Failed to add topper. Check Firebase Storage rules.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f5f7fa', border: '1px solid var(--ngt-border)', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
      <h5 style={{ color: 'var(--ngt-navy)', marginBottom: '1rem' }}><i className="fas fa-plus me-2"></i>Add New Topper</h5>
      {msg && (
        <div className={msg.startsWith('success') ? 'ngt-alert-success' : 'ngt-alert-error'} style={{ marginBottom: '1rem' }}>
          {msg.split(':')[1]}
        </div>
      )}
      <div className="row g-2">
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Student Name *</label>
          <input style={inp} placeholder="e.g. Priya Sharma" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        </div>
        <div className="col-md-3">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Percentage / Score *</label>
          <input style={inp} placeholder="e.g. 95.6" value={form.percentage}
            onChange={e => setForm(p => ({ ...p, percentage: e.target.value }))} />
        </div>
        <div className="col-md-3">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Year</label>
          <input style={inp} placeholder="e.g. 2026" value={form.year}
            onChange={e => setForm(p => ({ ...p, year: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Course / Exam *</label>
          <select style={inp} value={form.course} onChange={e => setForm(p => ({ ...p, course: e.target.value }))}>
            <option value="">— Select Course —</option>
            {['SSC', 'CBSE', 'JEE', 'NEET', 'MH-CET', 'Commerce', 'Other'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>School / College</label>
          <input style={inp} placeholder="e.g. St. Mary's High School" value={form.school}
            onChange={e => setForm(p => ({ ...p, school: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Photo (optional)</label>
          <input type="file" style={inp} accept="image/*"
            onChange={e => setImageFile(e.target.files[0])} />
        </div>
      </div>
      <button type="submit" className="btn-ngt-primary" style={{ padding: '10px 28px' }} disabled={saving}>
        {saving ? <><i className="fas fa-spinner fa-spin me-1"></i> Saving…</> : <><i className="fas fa-plus me-1"></i> Add Topper</>}
      </button>
    </form>
  );
};

/* ── Add Faculty Form ────────────────────────────────────────── */
const AddFacultyForm = ({ onAdded }) => {
  const empty = { name: '', subject: '', qualification: '', experience: '' };
  const [form, setForm] = useState(empty);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.subject) {
      setMsg('error:Name and subject are required.');
      return;
    }
    setSaving(true);
    setMsg('');
    try {
      const doc = await addFaculty({ ...form, imageFile });
      setMsg('success:Faculty member added successfully!');
      setForm(empty);
      setImageFile(null);
      onAdded({ id: doc.id, ...form, imageURL: '' });
    } catch (err) {
      setMsg('error:Failed to add faculty. Check Firebase Storage rules.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f5f7fa', border: '1px solid var(--ngt-border)', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
      <h5 style={{ color: 'var(--ngt-navy)', marginBottom: '1rem' }}><i className="fas fa-plus me-2"></i>Add New Faculty Member</h5>
      {msg && (
        <div className={msg.startsWith('success') ? 'ngt-alert-success' : 'ngt-alert-error'} style={{ marginBottom: '1rem' }}>
          {msg.split(':')[1]}
        </div>
      )}
      <div className="row g-2">
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Full Name *</label>
          <input style={inp} placeholder="e.g. Dr. Rahul Singh" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Subject *</label>
          <input style={inp} placeholder="e.g. Mathematics" value={form.subject}
            onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Qualification</label>
          <input style={inp} placeholder="e.g. M.Sc. Mathematics, IIT Bombay" value={form.qualification}
            onChange={e => setForm(p => ({ ...p, qualification: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Experience</label>
          <input style={inp} placeholder="e.g. 12 years of teaching experience" value={form.experience}
            onChange={e => setForm(p => ({ ...p, experience: e.target.value }))} />
        </div>
        <div className="col-12">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ngt-navy)', textTransform: 'uppercase' }}>Photo (optional)</label>
          <input type="file" style={inp} accept="image/*"
            onChange={e => setImageFile(e.target.files[0])} />
        </div>
      </div>
      <button type="submit" className="btn-ngt-primary" style={{ padding: '10px 28px' }} disabled={saving}>
        {saving ? <><i className="fas fa-spinner fa-spin me-1"></i> Saving…</> : <><i className="fas fa-plus me-1"></i> Add Faculty</>}
      </button>
    </form>
  );
};

/* ── Main Dashboard ──────────────────────────────────────────── */
const AdminDash = () => {
  const navigate = useNavigate();
  const [user, setUser]           = useState(null);
  const [tab, setTab]             = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [toppers, setToppers]     = useState([]);
  const [faculty, setFaculty]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (u) setUser(u);
      else navigate('/login');
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([getAllEnquiries(), getPendingTestimonies(), getAllToppers(), getAllFaculty()])
      .then(([e, t, tp, f]) => {
        setEnquiries(e); setTestimonies(t); setToppers(tp); setFaculty(f);
      })
      .catch(err => console.error('Load error:', err))
      .finally(() => setLoading(false));
  }, [user]);

  const handleLogout          = async () => { await adminLogout(); navigate('/login'); };
  const handleDeleteEnquiry   = async id => { await deleteEnquiry(id); setEnquiries(p => p.filter(e => e.id !== id)); };
  const handleApproveTestimony= async id => { await approveTestimony(id); setTestimonies(p => p.filter(t => t.id !== id)); };
  const handleDeleteTestimony = async id => { await deleteTestimony(id); setTestimonies(p => p.filter(t => t.id !== id)); };
  const handleDeleteTopper    = async (id, url) => { await deleteTopper(id, url); setToppers(p => p.filter(t => t.id !== id)); };
  const handleDeleteFaculty   = async (id, url) => { await deleteFaculty(id, url); setFaculty(p => p.filter(f => f.id !== id)); };

  if (!user) return null;
  if (loading) return <div className="ngt-spinner mt-5"></div>;

  const TABS = [
    { key: 'enquiries',   label: 'Enquiries',           count: enquiries.length,   icon: 'fas fa-inbox' },
    { key: 'testimonies', label: 'Pending Testimonials', count: testimonies.length, icon: 'fas fa-comment-dots' },
    { key: 'toppers',     label: 'Toppers',              count: toppers.length,     icon: 'fas fa-trophy' },
    { key: 'faculty',     label: 'Faculty',              count: faculty.length,     icon: 'fas fa-chalkboard-teacher' },
  ];

  return (
    <>
      <div className="ngt-page-banner" style={{ paddingBottom: '1.5rem' }}>
        <h1>Admin Dashboard</h1>
        <p>Nalanda Group Tuitions — Content Management</p>
      </div>

      <div className="container py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <p style={{ color: 'var(--ngt-grey)', margin: 0 }}>
            Logged in as: <strong>{user.email}</strong>
          </p>
          <button onClick={handleLogout} className="btn-ngt-outline" style={{ fontSize: '0.88rem', padding: '8px 20px' }}>
            <i className="fas fa-sign-out-alt me-1"></i> Logout
          </button>
        </div>

        {/* Stat Cards */}
        <div className="row g-3 mb-4">
          {TABS.map(t => (
            <div key={t.key} className="col-6 col-md-3">
              <div className="ngt-card text-center"
                style={{ cursor: 'pointer', borderColor: tab === t.key ? 'var(--ngt-navy)' : '', borderWidth: tab === t.key ? '2px' : '1px' }}
                onClick={() => setTab(t.key)}>
                <i className={t.icon} style={{ fontSize: '1.5rem', color: 'var(--ngt-navy)' }}></i>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--ngt-navy)', margin: '0.3rem 0' }}>{t.count}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--ngt-grey)' }}>{t.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tab Buttons */}
        <div className="d-flex gap-2 flex-wrap mb-4">
          {TABS.map(t => (
            <button key={t.key}
              className={tab === t.key ? 'btn-ngt-primary' : 'btn-ngt-outline'}
              style={{ fontSize: '0.88rem', padding: '8px 18px' }}
              onClick={() => setTab(t.key)}>
              <i className={`${t.icon} me-1`}></i> {t.label}
            </button>
          ))}
        </div>

        {/* ── Enquiries Tab ── */}
        {tab === 'enquiries' && (
          <div>
            <h4 style={{ color: 'var(--ngt-navy)', marginBottom: '1rem' }}>
              Student Enquiries ({enquiries.length})
            </h4>
            {enquiries.length === 0
              ? <p style={{ color: 'var(--ngt-grey)' }}>No enquiries yet.</p>
              : (
                <div className="table-responsive">
                  <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
                    <thead style={{ background: 'var(--ngt-navy)', color: '#fff' }}>
                      <tr><th>Name</th><th>Phone</th><th>Email</th><th>Course</th><th>Message</th><th>Delete</th></tr>
                    </thead>
                    <tbody>
                      {enquiries.map(e => (
                        <tr key={e.id}>
                          <td>{e.name}</td>
                          <td><a href={`tel:${e.phone}`}>{e.phone}</a></td>
                          <td>{e.email}</td>
                          <td><span className="ngt-badge">{e.course}</span></td>
                          <td style={{ maxWidth: '220px', fontSize: '0.85rem' }}>{e.message}</td>
                          <td>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteEnquiry(e.id)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        )}

        {/* ── Testimonials Tab ── */}
        {tab === 'testimonies' && (
          <div>
            <h4 style={{ color: 'var(--ngt-navy)', marginBottom: '1rem' }}>
              Pending Testimonials ({testimonies.length})
            </h4>
            {testimonies.length === 0
              ? <p style={{ color: 'var(--ngt-grey)' }}>No pending testimonials.</p>
              : (
                <div className="row g-3">
                  {testimonies.map(t => (
                    <div key={t.id} className="col-md-6 col-lg-4">
                      <div className="ngt-card h-100">
                        <p style={{ fontStyle: 'italic', fontSize: '0.92rem', color: 'var(--ngt-text)' }}>"{t.message}"</p>
                        <hr className="ngt-divider" />
                        <strong>{t.name}</strong>
                        <span className="ngt-badge ms-2">{t.course}</span>
                        {t.batch && <span style={{ color: 'var(--ngt-grey)', fontSize: '0.85rem' }}> · {t.batch}</span>}
                        <div className="d-flex gap-2 mt-3">
                          <button className="btn btn-sm btn-success flex-fill" onClick={() => handleApproveTestimony(t.id)}>
                            <i className="fas fa-check me-1"></i> Approve
                          </button>
                          <button className="btn btn-sm btn-danger flex-fill" onClick={() => handleDeleteTestimony(t.id)}>
                            <i className="fas fa-times me-1"></i> Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ── Toppers Tab ── */}
        {tab === 'toppers' && (
          <div>
            <h4 style={{ color: 'var(--ngt-navy)', marginBottom: '1.5rem' }}>
              Toppers ({toppers.length})
            </h4>

            <AddTopperForm onAdded={t => setToppers(p => [t, ...p])} />

            {toppers.length === 0
              ? <p style={{ color: 'var(--ngt-grey)' }}>No toppers added yet. Use the form above.</p>
              : (
                <div className="row g-3">
                  {toppers.map(t => (
                    <div key={t.id} className="col-6 col-md-4 col-lg-3">
                      <div className="ngt-card text-center">
                        {t.imageURL
                          ? <img src={t.imageURL} alt={t.name} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', margin: '0 auto 0.8rem', display: 'block' }} />
                          : <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--ngt-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.8rem' }}>
                              <i className="fas fa-user-graduate" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.8rem' }}></i>
                            </div>
                        }
                        <strong style={{ display: 'block', color: 'var(--ngt-navy)' }}>{t.name}</strong>
                        <span style={{ color: 'var(--ngt-grey)', fontSize: '0.82rem' }}>{t.course}{t.year ? ` · ${t.year}` : ''}</span><br />
                        <span className="ngt-badge mt-1">{t.percentage}%</span>
                        {t.school && (
                          <p style={{ color: '#888', fontSize: '0.78rem', fontStyle: 'italic', margin: '0.3rem 0 0' }}>
                            <i className="fas fa-school me-1" style={{ color: '#b8860b' }}></i>{t.school}
                          </p>
                        )}
                        <div className="mt-2">
                          <button className="btn btn-sm btn-danger w-100" onClick={() => handleDeleteTopper(t.id, t.imageURL)}>
                            <i className="fas fa-trash me-1"></i> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ── Faculty Tab ── */}
        {tab === 'faculty' && (
          <div>
            <h4 style={{ color: 'var(--ngt-navy)', marginBottom: '1.5rem' }}>
              Faculty Members ({faculty.length})
            </h4>

            <AddFacultyForm onAdded={f => setFaculty(p => [...p, f])} />

            {faculty.length === 0
              ? <p style={{ color: 'var(--ngt-grey)' }}>No faculty added yet. Use the form above.</p>
              : (
                <div className="row g-3">
                  {faculty.map(f => (
                    <div key={f.id} className="col-6 col-md-4 col-lg-3">
                      <div className="ngt-card text-center">
                        {f.imageURL
                          ? <img src={f.imageURL} alt={f.name} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', margin: '0 auto 0.8rem', display: 'block' }} />
                          : <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--ngt-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.8rem' }}>
                              <i className="fas fa-user-tie" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.8rem' }}></i>
                            </div>
                        }
                        <strong style={{ display: 'block', color: 'var(--ngt-navy)' }}>{f.name}</strong>
                        <span style={{ color: 'var(--ngt-gold)', fontSize: '0.85rem', fontWeight: 600 }}>{f.subject}</span>
                        {f.qualification && <p style={{ color: 'var(--ngt-grey)', fontSize: '0.8rem', marginTop: '0.3rem', marginBottom: 0 }}>{f.qualification}</p>}
                        <div className="mt-2">
                          <button className="btn btn-sm btn-danger w-100" onClick={() => handleDeleteFaculty(f.id, f.imageURL)}>
                            <i className="fas fa-trash me-1"></i> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

      </div>
    </>
  );
};

export default AdminDash;